import { Button } from '@heidi/design-system/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { focusRing } from '@heidi/design-system/styles';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsComposerGhostQueueEnabled } from '../../../flags';
import { queuedMessageListMessages } from './QueuedMessageList.messages';
import { QueuedMessageListCard } from './QueuedMessageListCard';
import { QueuedMessageRow } from './QueuedMessageRow';

export interface QueuedMessageListItem {
  id: string;
  /** Visible row text. Display only; inline edit uses `editableText`. */
  text: string;
  secondaryText?: string;
  /**
   * Full message body the inline editor may change.
   * Omit it when the visible text is a preview that must not be written back.
   */
  editableText?: string;
}

export interface QueuedMessageListProps {
  items: ReadonlyArray<QueuedMessageListItem>;
  onSendNow: (id: string) => void;
  onRemove: (id: string) => void;
  /** Kill-switch card Clear all. Ghost UI never renders a Clear control. */
  onClear: () => void;
  onEdit?: (id: string, content: string) => void;
  onMoveToFront?: (id: string) => void;
  /** Notifies when a row enters/leaves edit so the owner can pause drain. */
  onEditingChange?: (id: string, isEditing: boolean) => void;
  autoSend?: { enabled: boolean; onToggle: () => void };
  className?: string;
  testId?: string;
}

/**
 * Shared Work/Evidence queue list. Ghost accordion when the kill switch is on;
 * bordered card fallback when ops turn it off.
 */
export const QueuedMessageList = (props: QueuedMessageListProps): JSX.Element | null => {
  const isGhostQueueEnabled = useIsComposerGhostQueueEnabled();
  if (!isGhostQueueEnabled) {
    return <QueuedMessageListCard {...props} />;
  }
  return <QueuedMessageListGhost {...props} />;
};

const QueuedMessageListGhost = ({
  items,
  onSendNow,
  onRemove,
  onEdit,
  onMoveToFront,
  onEditingChange,
  autoSend,
  className,
  testId,
}: QueuedMessageListProps): JSX.Element | null => {
  const intl = useIntl();
  const queueCount = items.length;
  /** Open by default so newly queued messages appear as truncated one-liners. */
  const [isExpanded, setIsExpanded] = useState(true);

  // Reset collapse between queue lifetimes (empty → items again).
  useEffect(() => {
    if (queueCount === 0) {
      setIsExpanded(true);
    }
  }, [queueCount]);

  if (queueCount === 0) return null;

  return (
    <div className={cn('flex w-full flex-col gap-0', className)} data-testid={testId}>
      <Collapsible
        open={isExpanded}
        onOpenChange={setIsExpanded}
        className="flex w-full flex-col gap-2"
      >
        <div className="flex w-full items-center gap-2">
          <CollapsibleTrigger
            className={cn(
              'flex min-w-0 flex-1 items-center justify-between gap-2 p-1',
              'rounded-md text-start text-text-secondary',
              focusRing
            )}
            aria-label={intl.formatMessage(
              isExpanded
                ? queuedMessageListMessages.collapseQueueAria
                : queuedMessageListMessages.expandQueueAria
            )}
            data-testid="queued-message-list-toggle"
          >
            <TypographyP2 className="m-0 truncate text-text-secondary">
              <FormattedMessage
                {...queuedMessageListMessages.queuedCount}
                values={{ count: queueCount }}
              />
            </TypographyP2>
            <ChevronDown
              className={cn(
                'size-4 shrink-0 text-text-tertiary',
                'motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out',
                'motion-reduce:transition-none',
                // Collapsed: point toward the end edge; mirror in RTL.
                !isExpanded && '-rotate-90 rtl:rotate-90'
              )}
              aria-hidden
            />
          </CollapsibleTrigger>
          {isExpanded && autoSend ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  aria-pressed={autoSend.enabled}
                  onClick={() => autoSend.onToggle()}
                  className={cn(
                    'h-auto shrink-0 gap-1.5 rounded-md px-2 py-1',
                    autoSend.enabled
                      ? 'bg-accent-forest/15 text-accent-forest'
                      : 'text-text-tertiary hover:bg-surface-hover hover:text-text-secondary'
                  )}
                >
                  <span
                    className={cn(
                      'size-1.5 rounded-full',
                      autoSend.enabled ? 'bg-accent-forest' : 'bg-text-tertiary'
                    )}
                    aria-hidden
                  />
                  <FormattedMessage {...queuedMessageListMessages.autoSend} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px]">
                {intl.formatMessage(
                  autoSend.enabled
                    ? queuedMessageListMessages.autoSendOnTooltip
                    : queuedMessageListMessages.autoSendOffTooltip
                )}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>

        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-none">
          {/* Same gap-2 as header→list so label→first row matches row→row. */}
          <div
            data-testid="queued-message-list-scroller"
            className="scrollbar-subtle flex max-h-32 flex-col gap-2 overflow-y-auto"
          >
            {items.map((item, index) => (
              <QueuedMessageRow
                key={item.id}
                id={item.id}
                text={item.text}
                secondaryText={item.secondaryText}
                editableText={item.editableText}
                index={index}
                onSendNow={onSendNow}
                onRemove={onRemove}
                onEdit={onEdit}
                onMoveToFront={onMoveToFront}
                onEditingChange={onEditingChange}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
